import { useEffect, useRef, useState } from 'react'
import { Icon } from './Icon'

/**
 * SLIDE TO CONFIRM — a destructive action that a stray tap cannot fire.
 *
 * Owner, 2026-09-28, on End this agent: the confirm button appeared at the BOTTOM of
 * the card, below the fold on a phone, so pressing End looked like it did nothing
 * (「在小螢幕的時候, 是完全無法注意到下面新增的那個按鈕的」). His design: the control
 * appears IN PLACE of the button that was pressed, and it is a slide — 「由左向右滑動的
 * 機制, 讓使用者需要滑動才能夠確認…這樣子就可以防範使用者誤觸」.
 *
 * Drag the knob to the right end and let go: confirmed. Let go anywhere short of the
 * end: it springs back and nothing happens. The keyboard gets the same deliberate
 * gesture — ArrowRight walks the knob, End jumps it to the end — so the control is
 * operable without a pointer and still cannot be fired by one keypress on the track.
 */

/** How far along the track counts as "all the way" (a thumb rarely lands on the pixel). */
const DONE_AT = 0.92
const KEY_STEP = 0.25

export function SlideToConfirm({
  label,
  busyLabel,
  busy = false,
  onConfirm,
}: {
  /** What sliding does, said on the track: "Slide to end worker". */
  label: string
  /** Said while the confirmed action is in flight. */
  busyLabel: string
  busy?: boolean
  onConfirm: () => void
}) {
  const track = useRef<HTMLDivElement>(null)
  const knob = useRef<HTMLDivElement>(null)
  const drag = useRef<{ startX: number; from: number } | null>(null)
  // Progress along the track, 0..1 — the one number the picture and the rule share.
  const [p, setP] = useState(0)
  const [dragging, setDragging] = useState(false)

  // IN VIEW the moment it appears. It replaces the button that was just pressed, but a
  // section above it that finishes loading at that instant (the card's Model block)
  // pushes it below the fold — measured on a phone-sized screen, 2026-09-28.
  useEffect(() => {
    track.current?.scrollIntoView?.({ block: 'nearest' })
  }, [])

  // A finished slide stays at the end while the action runs; a failed one comes back
  // so it can be tried again.
  useEffect(() => {
    if (!busy) setP(0)
  }, [busy])

  const room = () => {
    const t = track.current?.clientWidth ?? 0
    const k = knob.current?.offsetWidth ?? 0
    return Math.max(1, t - k - 8)
  }

  const finish = (at: number) => {
    if (at >= DONE_AT) {
      setP(1)
      onConfirm()
    } else {
      setP(0)
    }
  }

  return (
    <div
      ref={track}
      className={`slide-confirm${busy ? ' busy' : ''}`}
      style={{ ['--p' as string]: String(p) }}
    >
      <div className="slide-confirm-fill" aria-hidden="true" />
      <span className="slide-confirm-label" aria-hidden="true">
        {busy ? busyLabel : label}
      </span>
      <div
        ref={knob}
        className={`slide-confirm-knob${dragging ? ' dragging' : ''}`}
        role="slider"
        tabIndex={busy ? -1 : 0}
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(p * 100)}
        aria-disabled={busy}
        onPointerDown={(e) => {
          if (busy) return
          e.currentTarget.setPointerCapture?.(e.pointerId)
          drag.current = { startX: e.clientX, from: p }
          setDragging(true)
        }}
        onPointerMove={(e) => {
          const d = drag.current
          if (!d) return
          setP(Math.min(1, Math.max(0, d.from + (e.clientX - d.startX) / room())))
        }}
        onPointerUp={(e) => {
          const d = drag.current
          drag.current = null
          setDragging(false)
          if (!d) return
          finish(Math.min(1, Math.max(0, d.from + (e.clientX - d.startX) / room())))
        }}
        onPointerCancel={() => {
          drag.current = null
          setDragging(false)
          setP(0)
        }}
        onKeyDown={(e) => {
          if (busy) return
          if (e.key === 'ArrowRight') {
            e.preventDefault()
            const next = Math.min(1, p + KEY_STEP)
            if (next >= 1) finish(1)
            else setP(next)
          } else if (e.key === 'End') {
            e.preventDefault()
            finish(1)
          } else if (e.key === 'ArrowLeft' || e.key === 'Home' || e.key === 'Escape') {
            e.preventDefault()
            setP(0)
          }
        }}
      >
        <Icon name="chevron_right" size={18} />
      </div>
    </div>
  )
}
